/**
 * Connect Bavimail: a partner (Bavlio first) sends its user to Clerk's OAuth
 * authorize endpoint on our instance; Clerk, finding no session, sends them to
 * this app's sign-in page with the authorize request in `redirect_url`.
 *
 * The partner's users are not Bavimail users yet and have never seen Bavimail,
 * so when the request belongs to a known partner the sign-in page does not
 * offer a form: it continues straight into that partner's identity provider
 * (the custom OAuth connection on our Clerk instance), which signs the user up
 * with a verified email in one hop, and then returns to the authorize request
 * for Clerk's consent screen.
 *
 * `redirect_url` is trusted only when it is a step of the OAuth flow on our own
 * Clerk instance: the authorize endpoint of the Frontend API, or the consent
 * page of the Account Portal, which is what Clerk actually names when it sends
 * a signed-out visitor to the sign-in page (the consent page checks the session
 * and resumes the authorize request). Anything else is ignored, and sign-in
 * lands on the dashboard as usual.
 */

export type ConnectPartner = {
  readonly name: string;
  readonly clientId: string;
  readonly strategy: `oauth_custom_${string}`;
};

export type AuthorizeRequest = {
  /** The authorize request to return to once a session exists. */
  readonly href: string;
  /** The partner behind it, when the request names a partner's OAuth client. */
  readonly partner: ConnectPartner | null;
};

const FRONTEND_API_PATHS = new Set(["/oauth/authorize", "/oauth/authorize/continue"]);
const ACCOUNT_PORTAL_PATH = "/oauth-consent";
const FRONTEND_API_SUBDOMAIN = "clerk.";
const ACCOUNT_PORTAL_SUBDOMAIN = "accounts.";

export type ConnectEnvironment = {
  readonly clerkFrontendApiUrl?: string;
  /**
   * The Account Portal of the same instance (`https://accounts.<domain>`). Derived from the
   * Frontend API host when unset, which is Clerk's own convention for a production instance.
   */
  readonly clerkAccountPortalUrl?: string;
  readonly bavlioOAuthClientId?: string;
};

export function connectPartners(environment: ConnectEnvironment): ConnectPartner[] {
  const bavlioClientId = environment.bavlioOAuthClientId?.trim();
  return bavlioClientId
    ? [{ name: "Bavlio", clientId: bavlioClientId, strategy: "oauth_custom_bavlio" }]
    : [];
}

function originOf(url: string | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.origin : null;
  } catch {
    return null;
  }
}

function accountPortalOrigin(environment: ConnectEnvironment): string | null {
  const configured = originOf(environment.clerkAccountPortalUrl);
  if (configured !== null) {
    return configured;
  }
  const frontendApi = originOf(environment.clerkFrontendApiUrl);
  if (frontendApi === null) {
    return null;
  }
  const host = new URL(frontendApi).host;
  return host.startsWith(FRONTEND_API_SUBDOMAIN)
    ? `https://${ACCOUNT_PORTAL_SUBDOMAIN}${host.slice(FRONTEND_API_SUBDOMAIN.length)}`
    : null;
}

function isFlowStep(url: URL, environment: ConnectEnvironment): boolean {
  if (url.origin === originOf(environment.clerkFrontendApiUrl)) {
    return FRONTEND_API_PATHS.has(url.pathname);
  }
  return url.origin === accountPortalOrigin(environment) && url.pathname === ACCOUNT_PORTAL_PATH;
}

export function parseAuthorizeRequest(
  redirectUrl: string | undefined,
  environment: ConnectEnvironment,
): AuthorizeRequest | null {
  if (!redirectUrl || originOf(environment.clerkFrontendApiUrl) === null) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(redirectUrl);
  } catch {
    return null;
  }
  if (!isFlowStep(url, environment)) {
    return null;
  }

  const clientId = url.searchParams.get("client_id");
  const partner =
    connectPartners(environment).find((candidate) => candidate.clientId === clientId) ?? null;
  return { href: url.href, partner };
}

function environmentFromImportMeta(): ConnectEnvironment {
  return {
    clerkFrontendApiUrl: import.meta.env.VITE_CLERK_FRONTEND_API_URL,
    clerkAccountPortalUrl: import.meta.env.VITE_CLERK_ACCOUNT_PORTAL_URL,
    bavlioOAuthClientId: import.meta.env.VITE_BAVLIO_OAUTH_CLIENT_ID,
  };
}

/** The authorize request behind a sign-in page visit, read from `redirect_url`. */
export function authorizeRequestFromSearch(redirectUrl: string | undefined) {
  return parseAuthorizeRequest(redirectUrl, environmentFromImportMeta());
}

export function buildLoginHref(search: { redirect_url: string; connect?: "manual" }): string {
  const params = new URLSearchParams({ redirect_url: search.redirect_url });
  if (search.connect) {
    params.set("connect", search.connect);
  }
  return `/login?${params.toString()}`;
}

export function buildSsoCallbackHref(authorizeHref: string): string {
  return `/login/sso-callback?${new URLSearchParams({ redirect_url: authorizeHref }).toString()}`;
}

export function buildCompleteSignUpHref(authorizeHref: string): string {
  return `/login/complete?${new URLSearchParams({ redirect_url: authorizeHref }).toString()}`;
}
